import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

import { ButtonFilled, ButtonOutlined, Flex, Text } from 'Components/Base';
import Loading from 'Components/Loading';
import ExamRoomProvider, { useExamRoomContext } from 'Context/ExamRoomContext';
import { PATHS } from 'Routes/Main/paths';
import { useDisclosure } from 'Util/Hooks';
import useCompatibleDisplay from 'Util/Hooks/useCompatibleDisplay';
import { EXAM_PAPER_TYPE } from 'Context/ExamRoomContext/mapper';
import ConfirmModal from 'Components/ConfirmModal';
import { SadIcon } from 'Components/Base/Icon/AnywhereSVGIcons';

import EmptyState from '../../../Components/EmptyState';
import ExamArticle from '../../../Components/ExamRoom/ExamArticle';
import ExamQuestion from '../../../Components/ExamRoom/ExamQuestion';
import SolutionDetailsModal from '../../../Components/ExamRoom/SolutionDetailsModal';
import Header from '../../../Components/Header';
import InnerLayout from '../../../Components/InnerLayout';

const Wrapper = styled.div`
  overflow: auto;

  /* Webkit browsers (Chrome, Safari) */
  &::-webkit-scrollbar {
    display: none;
  }

  /* Firefox */
  scrollbar-width: none;
`;

const StyledButtonOutline = styled((props) => (
  <ButtonOutlined color="white" variant={props.isMobile ? 'small' : 'medium'} {...props} />
))`
  ${({ theme, isMobile }) => css`
    border-radius: 300px;
    background-color: ${theme.colors.white};
    color: ${theme.colors.text};
    padding: ${({ theme }) =>
      `${isMobile ? theme.space[1] : theme.space[2]}px ${
        isMobile ? theme.space[2] : theme.space[3]
      }px`};
    height: ${isMobile ? '40px' : '48px'};
    &:hover {
      background-color: ${theme.colors.greyscale.grey5};
    }
  `}
`;

const StyledButtonFilled = styled((props) => (
  <ButtonFilled
    color="backgroundNeutral"
    variant={props.isMobile ? 'small' : 'medium'}
    {...props}
  />
))`
  ${({ theme, isMobile }) => css`
    border-radius: 300px;
    height: ${isMobile ? '40px' : '48px'};
    &:not(:disabled):hover {
      background-color: ${theme.colors.greyscale.grey1};
    }
  `}
`;

const ExamRoom = () => {
  const {
    loading,
    error,
    currentExamItem,
    handleNextExamItem,
    handleSelectAnswer,
    handleSubmitAnswer,
    openSolutionDetailsModal,
    onOpenSolutionDetailsModal,
    correctQuestionSolution,
    selectedChoices,
    currentQuestionNumber,
    totalQuestionAmount,
    errorGradeExamPaper,
    loadingGradeExamPaper,
    setErrorGradeExamPaper,
    errorSubmitExamPaper,
    loadingSubmitExamPaper,
    setErrorSubmitExamPaper,
  } = useExamRoomContext();
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const history = useHistory();
  const ref = React.useRef(null);
  const { t } = useTranslation('guidedPractice');
  const { isMobile } = useCompatibleDisplay();
  const [isErrorExamRoomModalOpen, , closeErrorExamRoomModal] = useDisclosure({
    isOpen: errorGradeExamPaper || errorSubmitExamPaper,
  });
  const isLoadingSubmit = loadingSubmitExamPaper || loadingGradeExamPaper;

  const handleCloseErrorExamRoomModal = () => {
    setErrorGradeExamPaper(false);
    setErrorSubmitExamPaper(false);
    closeErrorExamRoomModal();
  };

  const handleNext = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setIsTransitioning(false);
    }, 500);
    handleNextExamItem();
  };

  React.useEffect(() => {
    ref.current?.scrollTo(0, 0);
  }, [currentExamItem]);

  if (loading) {
    return <Loading />;
  } else if (error) {
    return (
      <EmptyState
        title="พบข้อผิดพลาด ลองทำแบบทดสอบใหม่อีกครั้ง!"
        description={error?.message || 'กรุณาย้อนกลับไปที่หน้าหลักและลองใหม่อีกครั้ง'}
        action={
          <ButtonFilled
            color={'secondary'}
            onClick={() => history.replace(PATHS.guidedPractice)}
            fontWeight={600}
            borderRadius={300}
            width={168}
            mt={4}
          >
            {t('BUTTON.BACK')}
          </ButtonFilled>
        }
      />
    );
  }

  const questionProgress = (
    <Flex alignItems="center" justifyContent="flex-end" mb={6}>
      <Text>
        คำถามที่ {currentQuestionNumber} จาก {totalQuestionAmount}
      </Text>
    </Flex>
  );

  const type = currentExamItem.type;
  if (type === EXAM_PAPER_TYPE.QUESTION) {
    const isSubmitAnswer = Object.keys(correctQuestionSolution).length > 0;

    const handleSubmitPrimaryOnClick = () => {
      if (!isSubmitAnswer) {
        handleSubmitAnswer();
      } else {
        handleNext();
      }
    };

    return (
      <Wrapper ref={ref} style={{ overflow: 'auto' }}>
        {questionProgress}
        <ExamQuestion
          containerClassName={isTransitioning ? 'fade-item fade-out' : 'fade-item fade-in'}
          question={currentExamItem.question}
          choices={currentExamItem.choices}
          selectedChoices={selectedChoices}
          correctAnswerIndices={isSubmitAnswer ? correctQuestionSolution.correctAnswerIndices : []}
          isCorrect={isSubmitAnswer ? correctQuestionSolution.isCorrect : null}
          onChoiceClick={(choice) => handleSelectAnswer(currentExamItem.id, choice)}
          footerAction={
            <Flex gridColumnGap={4} justifyContent="flex-end">
              {isSubmitAnswer ? (
                <>
                  <StyledButtonOutline isMobile={isMobile} onClick={onOpenSolutionDetailsModal}>
                    {t('BUTTON.SEE_SOLUTION')}
                  </StyledButtonOutline>
                  <StyledButtonFilled
                    isMobile={isMobile}
                    disabled={isLoadingSubmit}
                    onClick={handleNext}
                  >
                    {t('BUTTON.GO')}
                  </StyledButtonFilled>
                </>
              ) : (
                <StyledButtonFilled
                  isMobile={isMobile}
                  disabled={!selectedChoices || selectedChoices.length === 0 || isLoadingSubmit}
                  onClick={handleSubmitAnswer}
                >
                  {t('BUTTON.SEND_ANSWER')}
                </StyledButtonFilled>
              )}
            </Flex>
          }
          variant={isSubmitAnswer ? 'solution' : 'default'}
          questionType={currentExamItem.questionType}
        />
        <SolutionDetailsModal
          muiProps={{
            isActive: openSolutionDetailsModal,
            onClose: handleNext,
          }}
          isCorrect={correctQuestionSolution.isCorrect}
          solution={correctQuestionSolution.solution}
          disabledNextButton={loadingGradeExamPaper}
        />
        <ConfirmModal
          titleText={t('EXAM_ROOM.ERROR_EXAM_ROOM.TITLE')}
          contentText={t('EXAM_ROOM.ERROR_EXAM_ROOM.DESCRIPTION')}
          primaryText={t('EXAM_ROOM.ERROR_EXAM_ROOM.CONFIRM')}
          secondaryText={t('EXAM_ROOM.ERROR_EXAM_ROOM.CANCEL')}
          primaryOnClick={handleSubmitPrimaryOnClick}
          disabledPrimaryButton={isLoadingSubmit}
          icon={<SadIcon />}
          muiProps={{
            onClose: handleCloseErrorExamRoomModal,
            modalPaperStyle: { maxWidth: 336, textAlign: 'center' },
            isActive: isErrorExamRoomModalOpen,
          }}
        />
      </Wrapper>
    );
  }
  if (type === EXAM_PAPER_TYPE.ARTICLE) {
    return (
      <Wrapper ref={ref} style={{ overflow: 'auto' }}>
        <Flex
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          gridRowGap={4}
          mt={8}
        >
          <ExamArticle
            article={currentExamItem.details}
            containerClassName={isTransitioning ? 'fade-item fade-out' : 'fade-item fade-in'}
          />
          <StyledButtonFilled isMobile={isMobile} onClick={handleNext}>
            {t('BUTTON.GO')}
          </StyledButtonFilled>
        </Flex>
      </Wrapper>
    );
  }
  return <div>Exam Item Type: {type} is not supported</div>; // TODO: Integration Empty State
};

const ExamRoomPage = () => {
  const { guidedPracticeUserId, examPaperId, sessionId } = useParams();
  const history = useHistory();
  const { t } = useTranslation('guidedPractice');
  const [
    isBackToGuidedPracticeModalOpen,
    openBackToGuidedPracticeModal,
    closeBackToGuidedPracticeModal,
  ] = useDisclosure();

  const handleBackToGuidedPractice = () => {
    closeBackToGuidedPracticeModal();
    history.replace(PATHS.guidedPractice);
  };

  return (
    <Flex data-testid={'exam-room'} height={'100vh'} overflow={'hidden'} flexDirection={'column'}>
      <Header onClose={openBackToGuidedPracticeModal} />
      <InnerLayout>
        <ExamRoomProvider
          sessionId={sessionId}
          redirectPath={`${PATHS.guidedPractice}/${guidedPracticeUserId}/exam-room/${examPaperId}/session/${sessionId}/results`}
        >
          <ExamRoom />
        </ExamRoomProvider>
      </InnerLayout>
      <ConfirmModal
        titleText={t('EXAM_ROOM.EXIT_EXAM_ROOM.TITLE')}
        contentText={t('EXAM_ROOM.EXIT_EXAM_ROOM.DESCRIPTION')}
        primaryText={t('EXAM_ROOM.EXIT_EXAM_ROOM.CONFIRM')}
        secondaryText={t('EXAM_ROOM.EXIT_EXAM_ROOM.CANCEL')}
        primaryOnClick={closeBackToGuidedPracticeModal}
        icon={<SadIcon />}
        muiProps={{
          onClose: handleBackToGuidedPractice,
          modalPaperStyle: { maxWidth: 336, textAlign: 'center' },
          isActive: isBackToGuidedPracticeModalOpen,
        }}
      />
    </Flex>
  );
};

export default ExamRoomPage;
